import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiPost, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import type { StripeConnectLinkResponse } from '@/types/settings';
import { settingsKeys } from '@/lib/queryKeys';

/**
 * Initiates Stripe Connect onboarding. On success, redirect to response.url.
 */
export function useStripeConnect() {
  return useApiMutation<StripeConnectLinkResponse, void>({
    mutationFn: () =>
      apiPost<StripeConnectLinkResponse>(operationsClient, settingsEndpoints.stripeConnect()),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    // The server says why: Connect not enabled on the Stripe account, an API
    // policy the account must allow. The fixed text hid it, and read the same
    // after the account had changed and the reason with it.
    onError: (error) => {
      toast.error('Failed to start Stripe Connect setup', { description: error.message });
    },
    showErrorToast: false,
  });
}

export function useDismissStripeConnect() {
  const queryClient = useQueryClient();
  return useApiMutation<{ dismissed: boolean }, void>({
    mutationFn: () =>
      apiDelete<{ dismissed: boolean }>(operationsClient, settingsEndpoints.stripeConnect()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.payments() });
      toast.success('Incomplete Stripe connection dismissed');
    },
  });
}
